/**
 * Invitations Hooks
 *
 * TanStack Query-backed hooks for the invites page. Reads share the
 * ['invitations', ...] cache; mutations invalidate it.
 *
 * The global query client never retries a 4xx (see retryTransient in
 * query-client.ts), so a 403 here — the role lacks create:invitations — fails
 * once instead of storming the endpoint, while a network drop still self-heals.
 * Errors are ApiClientError, so callers can branch on `error.isForbidden()` etc.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '../auth/auth0-provider';
import {
  invitationsApi,
  type InviteRow, type InviteQuota, type AvailableCoupons,
  type CreateInviteRequest, type CreateInviteResponse,
} from '@/api/invitationsApi';
import type { ApiClientError } from '@/api/errors';
import { DEFAULT_PER_PAGE, type Paginated, type PaginationParams } from '@/api/pagination';

const INV = ['invitations'] as const;

export function useInviteQuota() {
  const { isAuthenticated, isSdkReady } = useAuth();
  return useQuery<InviteQuota, ApiClientError>({
    queryKey: [...INV, 'quota'],
    queryFn: () => invitationsApi.getQuota(),
    enabled: isAuthenticated && isSdkReady,
  });
}

export function useInvitesList(params: PaginationParams = {}) {
  const { isAuthenticated, isSdkReady } = useAuth();
  const page = params.page ?? 1;
  const perPage = params.perPage ?? DEFAULT_PER_PAGE;
  return useQuery<Paginated<InviteRow>, ApiClientError>({
    queryKey: [...INV, 'list', { page, perPage }],
    queryFn: () => invitationsApi.listInvites({ page, perPage }),
    enabled: isAuthenticated && isSdkReady,
    placeholderData: keepPreviousData,
  });
}

export function useAvailableCoupons() {
  const { isAuthenticated, isSdkReady } = useAuth();
  return useQuery<AvailableCoupons, ApiClientError>({
    queryKey: [...INV, 'available-coupons'],
    queryFn: () => invitationsApi.getAvailableCoupons(),
    enabled: isAuthenticated && isSdkReady,
    staleTime: 60_000,
  });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();
  return useMutation<CreateInviteResponse, ApiClientError, CreateInviteRequest>({
    mutationFn: (request) => invitationsApi.createInvite(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INV }),
  });
}

export function useResendInvite() {
  const queryClient = useQueryClient();
  return useMutation<{ email_send_failed: boolean }, ApiClientError, string>({
    mutationFn: (id) => invitationsApi.resendInvite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INV }),
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();
  return useMutation<{ revoked: true }, ApiClientError, string>({
    mutationFn: (id) => invitationsApi.revokeInvite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INV }),
  });
}
